import * as React from 'react';
import { cn } from '../lib/cn';
import { Button } from '../primitives/Button';
import { Card } from '../primitives/Card';

/**
 * UniversalFilterBar — the filter row above a table or a KPI group.
 *
 * LAYOUT ONLY (architect ruling AR-3). No data fetching, no date arithmetic, no
 * knowledge of any domain column. The moment a design-system component knows
 * what `commission_month` is, every consumer inherits sales semantics and the
 * package stops being a design system.
 *
 * `tone="hairline"` deliberately: a full Card would give control chrome the same
 * visual weight as content, and chrome should recede.
 *
 * Renders NOTHING when it has no slots — REQUIREMENTS F-3. An inert filter bar
 * is worse than no filter bar, because it implies a control the screen doesn't
 * actually offer.
 *
 * Placement follows the proximity principle (AR-4 / Rule S-2): a filter's blast
 * radius is the container it sits in. Put it inside the thing it controls, not
 * above a page where most tiles will ignore it.
 */

export interface UniversalFilterBarProps {
  period?: React.ReactNode;
  company?: React.ReactNode;
  product?: React.ReactNode;
  extra?: React.ReactNode;
  /** Non-default filters currently applied; drives the summary and Clear. */
  activeCount?: number;
  onClear?: () => void;
  /** Wrap in a Card. Pass false when embedding in a card that already exists. */
  bordered?: boolean;
  className?: string;
}

function UniversalFilterBar({
  period,
  company,
  product,
  extra,
  activeCount = 0,
  onClear,
  bordered = true,
  className,
}: UniversalFilterBarProps) {
  const hasSlots = !!(period || company || product || extra);
  if (!hasSlots) return null;

  const body = (
    <div className={cn('flex flex-wrap items-start justify-between gap-3', className)}>
      <div className="min-w-0 flex-1">{period}</div>

      {(company || product || extra) && (
        <div className="flex flex-wrap items-end gap-2">
          {company}
          {product}
          {extra}
        </div>
      )}

      {activeCount > 0 && onClear && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="tabular-nums">
            {activeCount} filter{activeCount === 1 ? '' : 's'}
          </span>
          <Button type="button" size="sm" variant="ghost" onClick={onClear}>
            Clear
          </Button>
        </div>
      )}
    </div>
  );

  if (!bordered) return body;

  return (
    <Card tone="hairline" interactive={false} className="p-3">
      {body}
    </Card>
  );
}

export { UniversalFilterBar };
